import { Link } from "react-router-dom";
import useReveal from "@/hooks/useReveal";
import { PRIMARY_SHOPIFY_URL } from "@/lib/shopify";

const PRODUCT_IMAGE =
  "https://customer-assets.emergentagent.com/job_afrqa-luxury/artifacts/0esx8z7g_fcda3137-3915-43e7-b3f9-6b5055ad7dbd.JPG";

export default function FeaturedProduct({ product, stock }) {
  const r1 = useReveal();
  const r2 = useReveal();

  const price = product ? product.price : 99;
  const remaining = stock ? stock.remaining : 187;

  return (
    <section
      id="featured"
      data-testid="featured-product-section"
      className="relative bg-[#0a0a0a] py-24 md:py-36"
    >
      <div className="max-w-[1400px] mx-auto px-6 md:px-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20 items-center">
          {/* Image */}
          <div
            ref={r1.ref}
            className={`reveal ${r1.inView ? "in-view" : ""} lg:col-span-7`}
          >
            <Link
              to="/product/africa-frame"
              className="block zoom-wrap prototype-photo bg-[#0c0c0c] aspect-[4/5] border border-[#1f1f1f] relative"
              data-testid="featured-product-image-link"
            >
              <img
                src={PRODUCT_IMAGE}
                alt="The Africa Frame Gold Edition — real prototype"
                className="w-full h-full object-cover"
              />
              <span
                aria-hidden
                className="absolute inset-0 pointer-events-none"
                style={{
                  background:
                    "radial-gradient(ellipse at center, transparent 55%, rgba(0,0,0,0.40) 100%)",
                }}
              />
              <span
                className="absolute top-5 left-5 inline-flex items-center gap-2 bg-black/60 backdrop-blur-md border border-[#d4af37]/50 text-[#d4af37] font-label py-1.5 px-3"
                data-testid="featured-prototype-tag"
              >
                <span className="pulse-dot" />
                Official Prototype
              </span>
            </Link>
          </div>

          {/* Text */}
          <div
            ref={r2.ref}
            className={`reveal reveal-delay-1 ${
              r2.inView ? "in-view" : ""
            } lg:col-span-5`}
          >
            <span
              className="inline-flex items-center gap-2 border border-[#d4af37]/70 text-[#d4af37] font-label py-1.5 px-3"
              data-testid="featured-product-badge"
            >
              <span className="pulse-dot" />
              Only 300 Pieces Available
            </span>

            <h2 className="mt-8 font-display text-[clamp(2.2rem,5vw,4.4rem)] leading-[1.02] tracking-tight text-white">
              The Africa{" "}
              <span className="font-editorial italic gold-text-gradient">
                Frame
              </span>
              <span className="gold text-[0.4em] align-super ml-1">™</span>
              <br />
              <span className="font-editorial italic text-[#e6d5b8] text-[0.55em]">
                Gold Edition.
              </span>
            </h2>

            <p className="mt-5 text-[#bdbdb7] text-lg leading-relaxed max-w-md font-light">
              Africa-shaped. Hexagonal-cut. Hand-finished in 18k gold-tone
              and matte acetate.
            </p>

            <div className="mt-10 flex items-center gap-10">
              <div>
                <span className="font-label text-[#8a8a85]">Edition Price</span>
                <p
                  className="mt-2 font-display text-white text-3xl"
                  data-testid="featured-product-price"
                >
                  ${price.toFixed(0)}{" "}
                  <span className="text-[#8a8a85] text-base">USD</span>
                </p>
              </div>
              <div className="w-px h-12 bg-[#2a2a2a]" />
              <div>
                <span className="font-label text-[#8a8a85]">Remaining</span>
                <p
                  className="mt-2 font-display text-white text-3xl"
                  data-testid="featured-remaining-count"
                >
                  {remaining}
                  <span className="text-[#8a8a85] text-base"> / 300</span>
                </p>
              </div>
            </div>

            <div className="mt-12 flex flex-wrap items-center gap-5">
              <a
                href={PRIMARY_SHOPIFY_URL}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="featured-preorder-btn"
                className="btn-gold"
              >
                Preorder Now
                <span aria-hidden>→</span>
              </a>
              <Link
                to="/product/africa-frame"
                data-testid="featured-details-link"
                className="font-label text-[#cfcfca] link-underline"
              >
                View Details
              </Link>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
